import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchMe } from '../api/auth'
import { userDepartmentsApi, type UserDepartmentsResponse } from '../api/userDepartments'
import { Button, Callout, Card, CardContent, CardHeader, Checkbox, DataTable, DataTableEmptyRow, StatusPill, useConfirm } from './ui'

/** Stessi ruoli della policy Admin del server (il server resta l'unico a decidere: gli altri ricevono 403). */
const ADMIN_ROLES = ['Administrator', 'EqualAdmin']

/**
 * F-162: rami degli utenti in blocco per la Libreria. Tabella utenti × rami con caselle, colonna "proposto dalle
 * pratiche" e pulsante "Applica la proposta" (aggiunge, non toglie mai). Solo Admin. Si monta dietro il flag
 * `libreria.rami_utenti`.
 */
export default function UserDepartmentsPanel() {
  const [isAdmin, setIsAdmin] = useState(false)
  const [data, setData] = useState<UserDepartmentsResponse | null>(null)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const askConfirm = useConfirm()

  const load = useCallback(() => {
    setError('')
    return userDepartmentsApi.list()
      .then(setData)
      .catch(e => { setData(null); setError(e instanceof Error ? e.message : 'Errore durante il caricamento dei rami') })
  }, [])

  useEffect(() => {
    let alive = true
    fetchMe()
      .then(me => { if (alive) setIsAdmin(!!me && ADMIN_ROLES.includes(me.role)) })
      .catch(() => { if (alive) setIsAdmin(false) })
    return () => { alive = false }
  }, [])

  useEffect(() => { if (isAdmin) load() }, [isAdmin, load])

  if (!isAdmin) return null

  async function toggle(userId: number, current: number[], departmentId: number, checked: boolean) {
    if (busyRef.current) return
    busyRef.current = true
    const next = checked ? [...current, departmentId] : current.filter(d => d !== departmentId)
    setBusy(true)
    setError('')
    setInfo('')
    try {
      const r = await userDepartmentsApi.set(userId, next)
      // Uso subito la risposta della PUT, poi aspetto il ricaricamento: niente clic sullo stato vecchio.
      setData(prev => prev && { ...prev, users: prev.users.map(u => u.userId === userId ? { ...u, departmentIds: r.departmentIds } : u) })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
      await load()
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  async function applyProposal() {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setError('')
    setInfo('')
    try {
      const r = await userDepartmentsApi.applyProposal()
      setInfo(r.added === 0 ? 'Nessun ramo da aggiungere.' : `Aggiunti ${r.added} rami a ${r.users} utenti.`)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante l\'applicazione della proposta')
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  const departments = data?.departments.filter(d => d.isActive) ?? []

  async function confirmProposal() {
    const summary = data?.proposalSummary
    const message = summary
      ? summary.departments === 0
        ? "Non c'è nessun ramo da aggiungere."
        : `Verranno aggiunti ${summary.departments} rami a ${summary.users} utenti. Nessun ramo viene tolto.`
      : 'Verranno aggiunti i rami proposti dalle pratiche. Nessun ramo viene tolto.'
    setInfo('')
    if (await askConfirm({ title: 'Applicare la proposta?', message, confirmLabel: 'Applica' })) await applyProposal()
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title="Rami degli utenti per la Libreria"
        subtitle="La proposta nasce dai rami delle pratiche dell'ultimo anno: aggiunge i rami mancanti e non ne toglie mai."
        actions={
          <Button variant="tonal" disabled={busy} onClick={() => void confirmProposal()}>
            Applica la proposta
          </Button>
        }
      />
      <CardContent>
        <div className="md-stack">
          {error && <Callout tone="error">{error}</Callout>}
          {info && <Callout tone="success">{info}</Callout>}
          {data && (
            <DataTable variant="plain" aria-label="Rami degli utenti">
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Ruolo</th>
                  {departments.map(d => <th key={d.id}>{d.name}</th>)}
                  <th>Proposto dalle pratiche</th>
                  <th>Da profilo e provvigioni</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.userId}>
                    <td>{u.name}</td>
                    <td className="text-muted">{u.role}</td>
                    {departments.map(d => (
                      <td key={d.id}>
                        <Checkbox
                          label={<span className="md-sr-only">{`${u.name}: ${d.name}`}</span>}
                          aria-label={`${u.name}: ${d.name}`}
                          disabled={busy}
                          checked={u.departmentIds.includes(d.id)}
                          onChange={e => toggle(u.userId, u.departmentIds, d.id, e.target.checked)}
                        />
                      </td>
                    ))}
                    <td>
                      {u.proposed.length === 0 ? <span className="text-muted">—</span>
                        : <div className="md-cluster">{u.proposed.map(p => {
                          const name = data.departments.find(d => d.id === p.departmentId)?.name ?? `#${p.departmentId}`
                          return <StatusPill key={p.departmentId} tone="neutral">{name} ({p.contracts})</StatusPill>
                        })}</div>}
                    </td>
                    <td>
                      {(u.otherSources ?? []).length === 0 ? <span className="text-muted">—</span>
                        : <div className="md-cluster">{(u.otherSources ?? []).map(o => {
                          const name = data.departments.find(d => d.id === o.departmentId)?.name ?? `#${o.departmentId}`
                          const label = o.source === 'agent_profile' ? 'profilo agente' : 'provvigioni'
                          return <StatusPill key={`${o.source}-${o.departmentId}`} tone="neutral">{name} ({label})</StatusPill>
                        })}</div>}
                    </td>
                  </tr>
                ))}
                {data.users.length === 0 && (
                  <DataTableEmptyRow colSpan={departments.length + 4} icon="group" title="Nessun utente attivo" />
                )}
              </tbody>
            </DataTable>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
